/** NFA Management — sticky header, breadcrumb, tabbed dense workspace. */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BarChart3, ChevronRight, FileCheck2, Inbox, LayoutDashboard, Shield, SlidersHorizontal } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useProject } from '@/contexts/ProjectContext';
import api from '@/lib/api';
import { useNfaTheme } from '@/components/nfa/nfaTheme';
import NfaRegister from '@/components/nfa/NfaRegister';
import { NfaAudit, NfaDashboard, NfaMatrix, NfaQueue } from '@/components/nfa/NfaPanels';
import NfaAnalytics from '@/components/nfa/NfaAnalytics';

const TABS = [
  ['analytics', 'NFA Analytics', BarChart3],
  ['register', 'Register', FileCheck2],
  ['queue', 'My Queue', Inbox],
  ['dashboard', 'Pending Approvals', LayoutDashboard],
  ['matrix', 'Approval Matrix', SlidersHorizontal],
  ['audit', 'Audit Trail', Shield],
];

export default function NfaManagement() {
  const T = useNfaTheme();
  const [searchParams] = useSearchParams();
  const requested = searchParams.get('view');
  const [tab, setTab] = useState(
    TABS.some(([k]) => k === requested) ? requested : 'analytics',
  );
  const [meta, setMeta] = useState(null);
  const [unread, setUnread] = useState(0);
  const { projectId, projectName } = useProject();

  // Hard scope: the NFA module only ever reads and writes the globally
  // selected project. Everything downstream receives a single-project meta.
  const scoped = useMemo(() => {
    if (!meta) return null;
    const match = (meta.projects || []).find((p) => p.project_id === projectId);
    const active = match || (projectId ? { project_id: projectId, name: projectName || projectId } : null);
    return { ...meta, projects: active ? [active] : [], activeProject: active };
  }, [meta, projectId, projectName]);
  const activeName = scoped?.activeProject?.name || projectName || projectId;

  useEffect(() => {
    api.get('/nfa/meta').then((r) => setMeta(r.data)).catch(() => setMeta(null));
    api.get('/nfa/inbox/notifications?unread_only=true')
      .then((r) => setUnread(r.data.unread)).catch(() => {});
  }, []);

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="nfa-management"
           style={{ fontFamily: 'Inter, sans-serif' }}>
        <header className="sticky top-14 z-20 -mx-6 px-6 py-3"
          style={{ background: T.page, borderBottom: `1px solid ${T.border}` }}>
          <div className="flex items-center gap-1.5 text-[10.5px] mb-1" style={{ color: T.muted }}>
            <span>Project Controls</span>
            <ChevronRight className="w-3 h-3" />
            <span style={{ color: T.body }}>NFA Management</span>
            <ChevronRight className="w-3 h-3" />
            <span className="px-1.5 py-0.5 rounded" data-testid="nfa-active-project"
              style={{ background: T.tint(T.primary), color: T.primary }}>{activeName || '—'}</span>
          </div>
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <h1 className="text-[22px] font-bold leading-tight"
                style={{ color: T.ink, fontFamily: '"Space Grotesk", Inter, sans-serif' }}>
                Note for Approval
              </h1>
              <p className="text-[11.5px] mt-0.5" style={{ color: T.body }}>
                Raise, route and audit management approvals — up to seven levels, driven by a
                configurable approval matrix and synchronised with SAP BTP.
              </p>
            </div>
            <div className="ml-auto flex items-center gap-2 text-[10px]" style={{ color: T.muted }}>
              {meta && (
                <>
                  <span className="px-2 py-0.5 rounded-full" data-testid="nfa-my-roles"
                    style={{ background: T.tint(T.primary), color: T.primary }}>
                    {(meta.myRoles || []).join(' · ') || 'no NFA role assigned'}
                  </span>
                  <span className="px-2 py-0.5 rounded-full" data-testid="nfa-drivers"
                    style={{ background: T.surfaceAlt, border: `1px solid ${T.border}` }}>
                    storage {meta.storageDriver} · scan {meta.scannerDriver} ·
                    notify {meta.notificationDriver} · OCR {meta.ocrAvailable ? 'on' : 'off'}
                  </span>
                  {unread > 0 && (
                    <span className="px-2 py-0.5 rounded-full" data-testid="nfa-unread"
                      style={{ background: T.tint(T.accent), color: T.accent }}>
                      {unread} notification{unread > 1 ? 's' : ''}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
          <nav className="flex items-center gap-1 mt-3 flex-wrap">
            {TABS.map(([key, label, Icon]) => (
              <button key={key} type="button" onClick={() => setTab(key)}
                data-testid={`nfa-tab-${key}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11.5px] font-semibold transition-colors"
                style={tab === key
                  ? { background: T.primary, color: '#fff' }
                  : { background: 'transparent', color: T.body, border: `1px solid ${T.border}` }}>
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </nav>
        </header>

        <div className="pb-10">
          {tab === 'analytics' && <NfaAnalytics projectId={projectId} projectName={activeName} />}
          {tab === 'register' && <NfaRegister meta={scoped} />}
          {tab === 'queue' && <NfaQueue projectId={projectId} />}
          {tab === 'dashboard' && <NfaDashboard projectId={projectId} projectName={activeName} />}
          {tab === 'matrix' && <NfaMatrix meta={scoped} />}
          {tab === 'audit' && <NfaAudit projectId={projectId} projectName={activeName} />}
        </div>
      </div>
    </DashboardLayout>
  );
}
